'use client';

/**
 * MarketDeskNotes — ZONE 4 (right rail) of the chain market page (market-v2 spec,
 * docs/design/market-prototype/market-v2.html).
 *
 * The desk's two OVERALL reads (`market_analysis/pulse.summary` and
 * `collection_analysis/pulse.summary`) as compact, 3-line-clamped cards with
 * datelines, expandable on tap. They sit BENEATH the pinned TVL chart — present,
 * never a wall. This is where round 1's lead-band prose went: judgment stays on
 * the page, but the numbers own the lead.
 *
 * Real-data law: a card renders only when its pulse doc is fresh (<24h); neither
 * fresh → the whole panel renders nothing (never an empty box).
 */

import React, { useState, useEffect } from 'react';
import type { MarketPulse } from '@/hooks/useMarketAnalysis';
import { getTimeAgo, isDataStale } from '@/hooks/useMarketAnalysis';
import type { CollectionPulse } from '@/hooks/useCollectionAnalysis';
import { scannable } from '@/components/market/market-shared';

function NoteCard({
  heading,
  body,
  facts,
  timestamp,
  hasMounted,
}: {
  heading: string;
  body: string;
  facts?: string;
  timestamp: string;
  hasMounted: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setExpanded((e) => !e)}
      aria-expanded={expanded}
      className="block w-full cursor-pointer border-b-[0.5px] border-gray-200 dark:border-divider p-3.5 text-left transition-colors last:border-b-0 hover:bg-gray-50 dark:hover:bg-white/[0.02]"
    >
      <h4 className="mb-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-amber-600 dark:text-scout-gold">
        {heading}
      </h4>
      <p
        className="font-mono text-[11px] leading-relaxed text-gray-600 dark:text-parchment/80"
        style={
          expanded
            ? undefined
            : { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }
        }
      >
        {scannable(body)}
      </p>
      <div className="mt-1.5 font-mono text-[8.5px] uppercase tracking-[0.06em] text-slate-ds">
        {facts && <>{facts} · </>}
        {hasMounted ? <>desk-produced <b className="font-semibold text-gray-600 dark:text-parchment-dim">{getTimeAgo(timestamp)}</b></> : 'desk-produced'}
        <span className="ml-1.5 text-amber-600 dark:text-scout-gold">{expanded ? '− less' : '+ expand'}</span>
      </div>
    </button>
  );
}

interface MarketDeskNotesProps {
  marketPulse: MarketPulse | null;
  collectionPulse: CollectionPulse | null;
}

export default function MarketDeskNotes({ marketPulse, collectionPulse }: MarketDeskNotesProps) {
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => setHasMounted(true), []);

  const tokenFresh = !!(marketPulse?.summary && !isDataStale(marketPulse.timestamp, 24));
  const nftFresh = !!(collectionPulse?.summary && !isDataStale(collectionPulse.timestamp, 24));

  // Quiet state: nothing fresh → no panel (never an empty box).
  if (!tokenFresh && !nftFresh) return null;

  const count = (tokenFresh ? 1 : 0) + (nftFresh ? 1 : 0);

  return (
    <div className="border-[0.5px] border-gray-200 dark:border-divider bg-white dark:bg-surface">
      <div className="flex items-center justify-between border-b-[0.5px] border-gray-200 dark:border-divider px-3.5 py-2.5">
        <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-amber-600 dark:text-scout-gold">
          ◆ Desk notes
        </span>
        <span className="font-mono text-[9px] uppercase tracking-[0.08em] text-slate-ds">{count} fresh</span>
      </div>

      {tokenFresh && marketPulse && (
        <NoteCard
          heading="Tokens"
          body={marketPulse.summary}
          facts={
            marketPulse.metrics
              ? `${marketPulse.metrics.gainersCount}↑ ${marketPulse.metrics.losersCount}↓ · volatility ${marketPulse.metrics.volatilityLevel}`
              : undefined
          }
          timestamp={marketPulse.timestamp}
          hasMounted={hasMounted}
        />
      )}
      {nftFresh && collectionPulse && (
        <NoteCard
          heading="NFTs"
          body={collectionPulse.summary}
          facts={
            collectionPulse.metrics?.totalVolume > 0
              ? `${collectionPulse.metrics.totalVolume.toFixed(2)} ETH 24h`
              : undefined
          }
          timestamp={collectionPulse.timestamp}
          hasMounted={hasMounted}
        />
      )}
    </div>
  );
}
